import { Box, BoxProps, CircularProgress, Stack, useTheme } from "@mui/material";
import { isNil } from "lodash-es";
import { CSSProperties, MouseEvent, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { ReactZoomPanPinchRef } from "react-zoom-pan-pinch";

import { VehicleVisualModelHelper } from "@/common/helpers/entity/vehicleVisualModel";
import { SvgHelper } from "@/common/helpers/svg";
import { TypeHelper } from "@/common/helpers/type";
import { useLatestRef } from "@/common/hooks/ref/useLatestRef";
import {
  SvgNewPointClickedParams,
  SvgPoint,
  SvgPointClickedParams,
  SvgPointFullCoords,
  SvgPointMovedParams,
  SvgPointStyle,
} from "@/common/ts/svg";
import { ResponsiveStyleValueCustom } from "@/theme/types";

import AppIconButton from "../../Button/AppIconButton";
import AppIcon from "../../Icons/AppIcon";
import ZoomableBlock from "../ZoomableBlock";

export interface GenericSvgViewerProps {
  svgUrl: Nil<string>;
  points: Nil<SvgPoint[]>;
  svgContainerSx?: BoxProps["sx"];
  svgProps?: {
    width?: ResponsiveStyleValueCustom<number | string>;
    height?: ResponsiveStyleValueCustom<number | string>;
    minWidth?: ResponsiveStyleValueCustom<number | string>;
    minHeight?: ResponsiveStyleValueCustom<number | string>;
    maxWidth?: ResponsiveStyleValueCustom<number | string>;
    maxHeight?: ResponsiveStyleValueCustom<number | string>;
    cursor?: CSSProperties["cursor"];
    /** Disables SVG inline hover effects defined by us (the effects are embedded into the SVG file). */
    isDisableHoverEffects?: boolean;
    point?: {
      cursor?: CSSProperties["cursor"];
    };
  };
  defaultPointStyle?: SvgPointStyle;
  isLoading?: boolean;
  isZoomEnabled?: boolean;
  /** Enables native resize. Control min/max width/height via SVG css props. */
  isResizeEnabled?: boolean;
  /** Enables point moving via dragging. */
  isPointDragEnabled?: boolean;
  debugKey?: string;
  customizations?: {
    topRightBlock?: ReactNode;
    bottomRightBlock?: ReactNode;
  };
  onNewPointClicked?: (params: SvgNewPointClickedParams) => void;
  onPointClicked?: (params: SvgPointClickedParams) => void;
  onPointMoved?: (params: SvgPointMovedParams) => void;
}

const logPrefix = "SvgViewer";

/** Generic component that renders SVG and points. */
export function GenericSvgViewer({
  points,
  svgUrl,
  svgContainerSx,
  svgProps,
  defaultPointStyle,
  isLoading = false,
  isZoomEnabled = false,
  isResizeEnabled = false,
  isPointDragEnabled = false,
  debugKey,
  customizations,
  onNewPointClicked,
  onPointClicked,
  onPointMoved,
}: GenericSvgViewerProps) {
  // hacks to fix closuring issues
  const onPointClickedRef = useLatestRef(onPointClicked);
  const onPointMovedRef = useLatestRef(onPointMoved);

  const theme = useTheme();

  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [isSvgLoading, setIsSvgLoading] = useState(!!svgUrl);

  const isLoadingComputed = isLoading || isSvgLoading;
  const isSvgContentLoaded = !isLoadingComputed && !!svgContent;
  const isControlsEnabled = isSvgContentLoaded;

  const transformComponentRef = useRef<ReactZoomPanPinchRef | null>(null);

  const svgElementRef = useRef<SVGSVGElement>(null);
  const svgCircleElementByIdMapRef = useRef<Record<string, SVGCircleElement | null>>({});

  const panDataRef = useRef({
    isPanning: false,
    startCoords: { x: 0, y: 0 },
  });
  const pointDragDataRef = useRef({
    isDragging: false,
    point: null as Nil<SvgPoint>,
    startE: null as Nil<MouseEvent<SVGGraphicsElement, globalThis.MouseEvent>>,
    startPointCoords: null as Nil<SvgPointFullCoords>,
    startClientCoords: { x: 0, y: 0 },
  });

  const svgDocumentParsed = useMemo(
    () => (svgContent ? new DOMParser().parseFromString(svgContent, "image/svg+xml") : undefined),
    [svgContent],
  );
  const svgElementParsed = svgDocumentParsed?.documentElement as Nil<SVGSVGElement>;

  const svgAttributes = useMemo(
    () => (svgElementParsed ? SvgHelper.parseAttributes(svgElementParsed) : undefined),
    [svgElementParsed],
  );
  const svgViewBox = svgAttributes?.viewBox;

  // load SVG by URL
  useEffect(() => {
    (async () => {
      if (svgUrl) {
        try {
          setIsSvgLoading(true);
          const svgContentRaw = await VehicleVisualModelHelper.fetchSvgContent(svgUrl);
          // const svgContentCleaned =
          //   SvgHelper.removeWidthAndHeightAttributesFromSvgContent(svgContentRaw);
          setSvgContent(svgContentRaw);
        } finally {
          setIsSvgLoading(false);
        }
      }
    })();
  }, [svgUrl]);

  const handleNewPointClick = (e: MouseEvent<SVGSVGElement, globalThis.MouseEvent>) => {
    console.log(logPrefix, "handleNewPointClick", e);
    if (!svgViewBox) {
      return;
    }

    const pointCoords = SvgHelper.getCursorPointCoords(e);
    if (!pointCoords) {
      return;
    }
    const isPointInsideViewBox = SvgHelper.isPointInsideViewBox({
      viewBox: svgViewBox,
      coords: pointCoords,
    });
    if (!isPointInsideViewBox) {
      return;
    }

    onNewPointClicked &&
      onNewPointClicked({
        e,
        coords: pointCoords,
        fullCoords: pointCoords,
        isPointInsideViewBox,
      });
  };

  const handlePointMoved = (params: {
    startE: MouseEvent<SVGGraphicsElement, globalThis.MouseEvent>;
    endE: MouseEvent<SVGGraphicsElement, globalThis.MouseEvent>;
    point: SvgPoint;
    startCoords: SvgPointFullCoords;
    endCoords: SvgPointFullCoords;
  }) => {
    console.log(logPrefix, "handlePointMoved", params);

    const cancelMove = () => {
      // reset coords
      const circleEl = svgCircleElementByIdMapRef.current[params.point.id];
      if (!circleEl) {
        return;
      }
      circleEl.setAttribute("cx", params.startCoords.x.toString());
      circleEl.setAttribute("cy", params.startCoords.y.toString());
    };

    onPointMovedRef.current &&
      onPointMovedRef.current({
        startE: params.startE,
        endE: params.endE,
        point: params.point,
        startCoords: params.startCoords,
        endCoords: params.endCoords,
        cancelMove,
        // point: params.point,
        // startCoords: params.startCoords,
        // endCoords: params.endCoords,
      });
  };

  // if (isLoadingComputed) {
  //   return (
  // <Box
  //   sx={{
  //     height: svgProps?.width ?? 400,
  //     width: svgProps?.width ?? 400,
  //     display: "flex",
  //     alignItems: "center",
  //     justifyContent: "center",
  //   }}
  // >
  //   <CircularProgress size='24px' />
  // </Box>
  //   );
  // }
  // if (!svgContent || !svgElementParsed || !svgAttributes || !svgViewBox) {
  //   return null;
  // }

  return (
    <Stack direction='row' spacing={1}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          gap: 1,
        }}
      >
        {/* SVG container */}
        <Box
          sx={{
            border: (th) => `1px solid ${th.palette.divider}`,
            borderRadius: (th) => th.shapeCustom.borderRadiusCard,
            ...svgContainerSx,

            position: "relative",

            // control SVG size via container size and make all descendants responsive
            width: svgProps?.width ?? svgAttributes?.width ?? 100,
            height: svgProps?.height ?? svgAttributes?.height ?? 100,
            minWidth: svgProps?.minWidth,
            minHeight: svgProps?.minHeight,
            maxWidth: svgProps?.maxWidth ?? "100%",
            maxHeight: svgProps?.maxHeight ?? "100%",
            overflow: "hidden", // required for resize
            resize: isResizeEnabled ? "both" : undefined,
          }}
        >
          {/* Loader. Show here to avoid content shift during loading. */}
          {isLoadingComputed && (
            <Box
              sx={{
                height: "100%",
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size='24px' />
            </Box>
          )}

          {/* No data alert */}
          {!isLoadingComputed && !isSvgContentLoaded && (
            <Box
              sx={{
                height: "100%",
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              No data
            </Box>
          )}

          {/* SVG zoom block */}
          {!isLoadingComputed && svgContent && svgElementParsed && svgAttributes && svgViewBox && (
            <>
              <ZoomableBlock
                wrapperRef={transformComponentRef}
                allWrappersSx={{
                  width: "100%",
                  height: "100%",
                }}
                initialScale={1}
                minScale={1}
                maxScale={10}
                smooth
                wheel={{
                  disabled: !isZoomEnabled,
                  step: 1,
                }}
                panning={{
                  disabled: !isZoomEnabled,
                  allowLeftClickPan: true,
                  allowMiddleClickPan: true,
                  allowRightClickPan: false,
                  wheelPanning: true,
                }}
                pinch={{
                  disabled: !isZoomEnabled,
                  step: 1,
                }}
                doubleClick={{
                  disabled: !isZoomEnabled,
                  mode: "reset",
                }}
              >
                {/* SVG */}
                <Box
                  component='svg'
                  ref={svgElementRef}
                  xmlns='http://www.w3.org/2000/svg'
                  {...{ "xmlns:svg": "http://www.w3.org/2000/svg" }}
                  version={svgElementParsed?.getAttribute("version") || undefined}
                  width={undefined} // ignore original width and height
                  height={undefined}
                  // viewBox={svgElementParsed.getAttribute("viewBox") || undefined}
                  viewBox={svgViewBox?.viewBox}
                  fill={svgElementParsed?.getAttribute("fill") || undefined}
                  preserveAspectRatio='xMidYMid meet' // important: force uniform scaling
                  sx={{
                    // keep aspect ratio by default by using auto height and auto margin,
                    // but we still must allow to stretch SVG via CSS if needed and handle it correctly.
                    width: "100%",
                    height: "auto",
                    margin: "auto", // center SVG in the container
                    cursor: svgProps?.cursor || undefined, // TODO: Vadym switch based on mode: view/edit

                    ...(svgProps?.isDisableHoverEffects
                      ? {
                          "& path": {
                            pointerEvents: "none",
                          },
                        }
                      : undefined),

                    // animations
                    "@keyframes svgCirclePulse": {
                      "0%,100%": { strokeWidth: 2.5 },
                      "50%": { strokeWidth: 4 },
                    },
                  }}
                  // NB: can't use onClick as it conflicts with panning
                  onMouseDown={(e) => {
                    console.log("svg.onMouseDown");

                    // manually track panning state to handle clicks correctly
                    panDataRef.current.isPanning = true;
                    panDataRef.current.startCoords.x = e.clientX;
                    panDataRef.current.startCoords.y = e.clientY;
                  }}
                  onMouseUp={(e) => {
                    // handle SVG panning end and edge case of point drag end (when mouse leaves point circle and onMouseUp fired out of the circle)
                    const isPanStartAndEndCoordsEqual =
                      panDataRef.current.startCoords.x == e.clientX &&
                      panDataRef.current.startCoords.y == e.clientY;
                    const isPointDragStartAndEndCoordsEqual =
                      pointDragDataRef.current.startClientCoords.x === e.clientX &&
                      pointDragDataRef.current.startClientCoords.y === e.clientY;

                    const isSvgClicked = isPanStartAndEndCoordsEqual;
                    const isPointDragged =
                      !isPanStartAndEndCoordsEqual &&
                      !isPointDragStartAndEndCoordsEqual &&
                      pointDragDataRef.current.isDragging;

                    panDataRef.current.isPanning = false;
                    pointDragDataRef.current.isDragging = false;

                    console.log("svg.onMouseUp", {
                      e,
                      isPanStartAndEndCoordsEqual,
                      isPointDragStartAndEndCoordsEqual,
                      isSvgClicked,
                      isPointDragged,
                    });

                    if (isSvgClicked) {
                      console.log("svg.click", { e });
                      handleNewPointClick(e);
                      return;
                    }

                    if (isPointDragged) {
                      const pointCoords = SvgHelper.getCursorPointCoords(e);

                      if (
                        pointDragDataRef.current.point &&
                        pointDragDataRef.current.startE &&
                        pointDragDataRef.current.startPointCoords &&
                        pointCoords
                      ) {
                        handlePointMoved({
                          startE: pointDragDataRef.current.startE,
                          endE: e,
                          point: pointDragDataRef.current.point,
                          startCoords: pointDragDataRef.current.startPointCoords,
                          endCoords: pointCoords,
                        });
                      }

                      pointDragDataRef.current.point = null;
                    }
                  }}
                  onMouseMove={(e) => {
                    e.preventDefault();

                    // handle point dragging
                    if (
                      isPointDragEnabled &&
                      pointDragDataRef.current.isDragging &&
                      pointDragDataRef.current.point
                    ) {
                      e.stopPropagation();

                      const circleEl =
                        svgCircleElementByIdMapRef.current[pointDragDataRef.current.point.id];
                      if (!circleEl) {
                        return;
                      }

                      // allow dragging points only over SVG area
                      const elsUnderCursor = document.elementsFromPoint(e.clientX, e.clientY);
                      const elsUnderCursorExclTheCircle = elsUnderCursor.filter(
                        (x) => x !== circleEl,
                      );
                      const isOverSvgEl = elsUnderCursorExclTheCircle.some(
                        (x) => x === svgElementRef.current,
                      );
                      if (!isOverSvgEl) {
                        return;
                      }

                      // allow dragging points only over non-empty SVG areas (<g />, <path />, etc. elements),
                      // so user can't eventually drop a point at empty SVG area.
                      const isOverAnySvgDescendantEl = elsUnderCursorExclTheCircle.some(
                        (x) => x instanceof SVGGraphicsElement && !(x instanceof SVGSVGElement),
                      );
                      if (!isOverAnySvgDescendantEl) {
                        return;
                      }

                      // update point position in DOM
                      const pointCoords = SvgHelper.getCursorPointCoords(e);
                      if (!pointCoords) {
                        return;
                      }

                      circleEl.setAttribute("cx", pointCoords.x.toString());
                      circleEl.setAttribute("cy", pointCoords.y.toString());
                    }
                  }}
                >
                  {/* Original SVG paths */}
                  {svgElementParsed && (
                    <g dangerouslySetInnerHTML={{ __html: svgElementParsed.innerHTML }} />
                  )}

                  {/* Points */}
                  {points?.map((point) => {
                    // ensure absolute coords are correct
                    const coordsAbsolute = SvgHelper.convertRelativeToAbsoluteCoords({
                      viewBox: svgViewBox,
                      coords: {
                        x: point.coords.xp,
                        y: point.coords.yp,
                      },
                    });
                    const style = {
                      ...defaultPointStyle,
                      ...point.style,
                    };
                    return (
                      <circle
                        key={point.id}
                        ref={(el) => {
                          svgCircleElementByIdMapRef.current[point.id] = el;
                        }}
                        id={point.id || undefined}
                        cx={coordsAbsolute.x}
                        cy={coordsAbsolute.y}
                        r={
                          style.circleRadius ??
                          (!isNil(style.circleRadiusPct)
                            ? `${style.circleRadiusPct}%`
                            : undefined) ??
                          4
                        }
                        // stroke='black'
                        // strokeWidth={1}
                        style={{
                          fill: style.color || "gray",
                          cursor: svgProps?.point?.cursor || "pointer",

                          // use pulsing animation to attract attention
                          ...(style.isAttentionRequired
                            ? {
                                fill: theme.palette.primary.dark,
                                stroke: theme.palette.primary.light,
                                strokeWidth: 5,
                                vectorEffect: "non-scaling-stroke",
                                animation: "svgCirclePulse 1.8s ease-in-out infinite",
                              }
                            : {}),
                        }}
                        // NB: can't use onClick as it conflicts with dragging
                        onMouseDown={(e) => {
                          console.log(logPrefix, "point.onMouseDown", {
                            e,
                            point,
                            coordsAbsolute,
                            pointDragData: pointDragDataRef.current,
                          });

                          e.stopPropagation();

                          pointDragDataRef.current.startE = e;
                          pointDragDataRef.current.startClientCoords = {
                            x: e.clientX,
                            y: e.clientY,
                          };

                          if (
                            !pointDragDataRef.current.isDragging &&
                            isPointDragEnabled &&
                            TypeHelper.isNilOrTrue(point.isDraggable)
                          ) {
                            pointDragDataRef.current.isDragging = true;
                            pointDragDataRef.current.point = point;
                            pointDragDataRef.current.startPointCoords = point.coords;
                          }
                        }}
                        onMouseUp={(e) => {
                          e.stopPropagation();

                          pointDragDataRef.current.isDragging = false;

                          const endClientCoords = { x: e.clientX, y: e.clientY };
                          const isStartAndEndCoordsEqual =
                            pointDragDataRef.current.startClientCoords.x === endClientCoords.x &&
                            pointDragDataRef.current.startClientCoords.y === endClientCoords.y;
                          const isClicked = isStartAndEndCoordsEqual;
                          const isDragged = !isStartAndEndCoordsEqual;

                          console.log(logPrefix, "point.onMouseUp", {
                            e,
                            point,
                            coordsAbsolute,
                            pointDragData: pointDragDataRef.current,
                            startClientCoords: pointDragDataRef.current.startClientCoords,
                            endClientCoords,
                            isStartAndEndCoordsEqual,
                            isClicked,
                            isDragged,
                          });

                          if (isClicked) {
                            console.log(logPrefix, "point.click", { point });
                            onPointClickedRef.current && onPointClickedRef.current({ e, point });
                          }

                          if (isDragged) {
                            const pointCoords = SvgHelper.getCursorPointCoords(e);

                            if (
                              pointDragDataRef.current.point &&
                              pointDragDataRef.current.startE &&
                              pointDragDataRef.current.startPointCoords &&
                              pointCoords
                            ) {
                              handlePointMoved({
                                startE: pointDragDataRef.current.startE,
                                endE: e,
                                point: pointDragDataRef.current.point,
                                startCoords: pointDragDataRef.current.startPointCoords,
                                endCoords: pointCoords,
                              });
                            }

                            pointDragDataRef.current.point = null;
                          }
                        }}
                      >
                        {(point.title || style.isAttentionRequired) && (
                          <title>{point.title || "Attention required!"}</title>
                        )}
                      </circle>
                    );
                  })}
                </Box>
              </ZoomableBlock>

              {/* Custom blocks */}
              {customizations?.topRightBlock && (
                <Box sx={{ position: "absolute", top: "1%", right: "1%" }}>
                  {customizations.topRightBlock}
                </Box>
              )}
              {customizations?.bottomRightBlock && (
                <Box sx={{ position: "absolute", bottom: "1%", right: "1%" }}>
                  {customizations.bottomRightBlock}
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Controls */}
        {isZoomEnabled && (
          <Stack
            direction='column'
            sx={{
              height: "100%",
              justifyContent: "flex-end",
              gap: 1,
              // ml: 1,
              // mt: "auto !important",
            }}
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            {/* Center view */}
            <Stack
              direction='column'
              sx={{
                p: 0.5,
                borderRadius: (th) => th.shapeCustom.borderRadiusCard,
                boxShadow: (th) => th.boxShadowCustom?.action[1],
              }}
            >
              <AppIconButton
                disabled={!isControlsEnabled}
                size='small'
                tooltipProps={{ title: "Center view", enterDelay: 300, enterNextDelay: 300 }}
                onClick={() => {
                  transformComponentRef.current?.centerView();
                }}
              >
                <AppIcon of='center' />
              </AppIconButton>
            </Stack>

            {/* Zoom in/out */}
            <Stack
              direction='column'
              sx={{
                gap: 1,
                p: 0.5,
                borderRadius: (th) => th.shapeCustom.borderRadiusCard,
                boxShadow: (th) => th.boxShadowCustom?.action[1],
              }}
            >
              <AppIconButton
                disabled={!isControlsEnabled}
                size='small'
                tooltipProps={{ title: "Zoom in", enterDelay: 300, enterNextDelay: 300 }}
                onClick={() => {
                  transformComponentRef.current?.zoomIn();
                }}
              >
                <AppIcon of='zoomIn' />
              </AppIconButton>

              <AppIconButton
                disabled={!isControlsEnabled}
                size='small'
                tooltipProps={{ title: "Zoom out", enterDelay: 300, enterNextDelay: 300 }}
                onClick={() => {
                  transformComponentRef.current?.zoomOut();
                }}
              >
                <AppIcon of='zoomOut' />
              </AppIconButton>
            </Stack>

            {/* Reset zoom */}
            <Stack
              direction='column'
              sx={{
                p: 0.5,
                borderRadius: (th) => th.shapeCustom.borderRadiusCard,
                boxShadow: (th) => th.boxShadowCustom?.action[1],
              }}
            >
              <AppIconButton
                disabled={!isControlsEnabled}
                size='small'
                tooltipProps={{ title: "Reset zoom", enterDelay: 300, enterNextDelay: 300 }}
                onClick={() => {
                  transformComponentRef.current?.resetTransform();
                }}
              >
                <AppIcon of='reset' />
              </AppIconButton>
            </Stack>
          </Stack>
        )}
      </Box>
    </Stack>
  );
}
